import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { fetchAttempts, fetchDelivery, fetchFailureReasonCodes } from '../api/client';
import { fetchOrgRoster } from '../api/hbs';
import type { FieldTask } from '../api/types';
import { useSession } from '../app/WorkforceContext';
import { useAsync } from '../app/useAsync';
import { namaLayak } from '../lib/display-name';
import { isSuccessfulAttempt, sortAttempts } from '../lib/delivery-board';
import { ProofPhotos } from './ProofPhotos';
import { reasonCodeLabel } from '../lib/delivery-form';
import { formatAccuracy, formatDateInZone, formatPlainDate, formatTimeInZone } from '../lib/format';
import { hasAnyPermission } from '../lib/navigation';

/**
 * Blok pengiriman pada halaman detail tugas — barang yang dibawa + riwayat percobaan.
 *
 * Dipasang hanya bila tugasnya memang punya detail pengiriman. Tugas non-pengiriman tidak
 * menampilkan bagian ini sama sekali, alih-alih menampilkan kartu kosong yang membuat orang
 * mengira ada data yang gagal dimuat.
 */
export function DeliverySection({
  task,
  taskStatus,
}: {
  readonly task: FieldTask;
  readonly taskStatus: string;
}): ReactNode {
  const session = useSession();
  const timezone = session.policy?.timezone ?? 'Asia/Jakarta';
  const canRead = hasAnyPermission(session.me.permissions, ['workforce.delivery.read']);
  const taskId = task.id;

  const { state } = useAsync(async () => {
    if (!canRead) return null;
    /*
     * Roster diambil di sini, bukan diteruskan dari TaskDetailPage — nama kurir (item 2, review
     * 12 Sep 2026) hanya berguna bagi peran yang JUGA berhak membaca delivery; menariknya untuk
     * kurir sendiri yang membuka tugasnya sendiri hanya membebani tanpa guna (namanya sendiri,
     * ia sudah tahu).
     */
    const [delivery, attempts, codes, roster] = await Promise.all([
      fetchDelivery(session.auth, taskId).catch(() => null),
      fetchAttempts(session.auth, taskId).catch(() => []),
      fetchFailureReasonCodes(session.auth).catch(() => []),
      fetchOrgRoster(session.auth.accessToken, session.me.organizationId).catch(() => []),
    ]);
    return { delivery, attempts, codes, roster };
  }, [session.auth, taskId, canRead]);

  // Diam sepenuhnya bila belum siap, tidak berhak, atau tugas ini memang bukan pengiriman.
  if (state.status !== 'ready' || state.data === null || state.data.delivery === null) {
    return null;
  }

  const { delivery, attempts, codes, roster } = state.data;
  const canFail = taskStatus === 'started' || taskStatus === 'arrived';

  const kurir = task.assigneeUserId
    ? (roster.find((m) => m.id === task.assigneeUserId)?.displayName ?? null)
    : null;
  const namaKurirLayak = namaLayak(kurir);

  /*
   * Nomor telepon TIDAK PERNAH hilang lagi (item 2, review 12 Sep 2026). Sebelumnya seluruh baris
   * "Kontak" hanya muncul bila `contactPerson` terisi — dan ERP shipment TIDAK PERNAH mengirim
   * nama penerima (`ErpShipmentIngestDto` tidak punya field itu sama sekali, hanya
   * `contactPhone`), jadi baris ini SELALU tersembunyi untuk setiap pengiriman dari ERP,
   * termasuk nomor teleponnya. Digabung jadi "Penerima" sesuai istilah Owner, ditampilkan bila
   * SALAH SATU (nama atau telepon) ada.
   */
  const penerimaNama = delivery.contactPerson;
  const penerimaTelepon = delivery.contactPhone;

  return (
    <>
      <section className="card">
        <h3>Pengiriman</h3>
        <dl>
          {delivery.deliveryOrderNumber ? (
            <>
              <dt>No. DO</dt>
              <dd>{delivery.deliveryOrderNumber}</dd>
            </>
          ) : null}
          {/*
            BUKAN tautan — lihat catatan tipe `salesOrderNumber` di `api/types.ts`. CRM belum
            punya rute detail atau pencarian Sales Order lewat URL untuk ditautkan balik.
          */}
          {delivery.salesOrderNumber ? (
            <>
              <dt>No. SO</dt>
              <dd>{delivery.salesOrderNumber}</dd>
            </>
          ) : null}
          {task.scheduledDate ? (
            <>
              <dt>Tanggal kirim</dt>
              <dd>{formatPlainDate(task.scheduledDate)}</dd>
            </>
          ) : null}
          {delivery.customerName ? (
            <>
              <dt>Pelanggan</dt>
              <dd>{delivery.customerName}</dd>
            </>
          ) : null}
          {namaKurirLayak ? (
            <>
              <dt>Kurir</dt>
              <dd>{namaKurirLayak}</dd>
            </>
          ) : null}
          {penerimaNama || penerimaTelepon ? (
            <>
              <dt>Penerima</dt>
              <dd>
                {penerimaNama ?? '(nama belum tercatat)'}
                {penerimaTelepon ? ` · ${penerimaTelepon}` : ''}
              </dd>
            </>
          ) : null}
          {/*
            Alamat tujuan SENGAJA TIDAK diulang di sini — `TaskDetailPage` sudah punya kartu
            "Tujuan" tersendiri (destinationName/destinationAddress + tautan peta) yang tampil
            untuk SEMUA jenis tugas, bukan hanya pengiriman. Menampilkannya lagi di sini akan
            mencetak alamat yang sama dua kali di halaman yang sama.
          */}
          {delivery.pickupLocationName || delivery.pickupAddress ? (
            <>
              <dt>Pickup</dt>
              <dd>{delivery.pickupLocationName ?? delivery.pickupAddress}</dd>
            </>
          ) : null}
        </dl>
        {delivery.deliveryNotes ? <p className="small">{delivery.deliveryNotes}</p> : null}
      </section>

      <section className="card">
        <h3>Barang</h3>
        {delivery.items.length === 0 ? (
          <p className="muted small">Tidak ada barang tercatat.</p>
        ) : (
          <table className="compare">
            <thead>
              <tr>
                <th scope="col">#</th>
                <th scope="col">Barang</th>
                <th scope="col">Jumlah</th>
              </tr>
            </thead>
            <tbody>
              {[...delivery.items]
                .sort((a, b) => a.lineNo - b.lineNo)
                .map((item) => (
                  <tr key={item.id}>
                    <th scope="row">{item.lineNo}</th>
                    <td>
                      {item.itemName}
                      {item.sku ? ` (${item.sku})` : ''}
                    </td>
                    <td>
                      {String(item.quantity)}
                      {item.unit ? ` ${item.unit}` : ''}
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        )}
      </section>

      <section className="card">
        <h3>Riwayat percobaan</h3>
        {attempts.length === 0 ? (
          <p className="muted small">Belum ada percobaan pengiriman.</p>
        ) : (
          <ul className="list">
            {/* Percobaan kedua TIDAK menimpa yang pertama — keduanya baris tersendiri. */}
            {sortAttempts(attempts).map((attempt) => (
              <li key={attempt.id} className="list-item">
                <div className="row space-between">
                  <strong>Percobaan #{attempt.attemptNo}</strong>
                  <span
                    className={isSuccessfulAttempt(attempt.outcome) ? 'badge ok' : 'badge warn'}
                  >
                    {isSuccessfulAttempt(attempt.outcome) ? 'Berhasil' : 'Gagal'}
                  </span>
                </div>
                <p className="muted small">
                  {formatDateInZone(attempt.occurredAtServer, timezone)}{' '}
                  {formatTimeInZone(attempt.occurredAtServer, timezone)}
                </p>
                {!isSuccessfulAttempt(attempt.outcome) ? (
                  <p className="small">{reasonCodeLabel(codes, attempt.failureReasonCodeId)}</p>
                ) : null}
                {attempt.failureDetail ? (
                  <p className="muted small">{attempt.failureDetail}</p>
                ) : null}
                {attempt.recipientName ? (
                  <p className="muted small">Diterima: {attempt.recipientName}</p>
                ) : null}
                {attempt.accuracyMeters !== null ? (
                  <p className="muted small">{formatAccuracy(attempt.accuracyMeters)}</p>
                ) : null}
                <ProofPhotos attemptNo={attempt.attemptNo} fileIds={attempt.photoFileIds ?? []} />
              </li>
            ))}
          </ul>
        )}

        {canFail ? (
          <div className="row">
            {taskStatus === 'arrived' ? (
              <Link className="button-link" to={`/tasks/${taskId}/proof`}>
                Rekam bukti &amp; selesaikan
              </Link>
            ) : null}
            <Link className="button-link" to={`/tasks/${taskId}/fail`}>
              Laporkan gagal
            </Link>
          </div>
        ) : null}
      </section>
    </>
  );
}
